import React from "react";
import { Card } from "react-bootstrap";
import { useSelector } from "react-redux";
import moment from "moment";
import { ReduxState } from "../../../redux";
import { capturePostHogEvent } from "../../../utils/posthog";
import { Job, JobType } from "../../../docuclipper/DocuclipperTypes";

const SHOW_AFTER_MS = 20_000;
const ROTATE_MS = 8_000;
const DISMISS_STORAGE_KEY = "jobInProgressInfoCardsDismissedAt";
const DISMISS_DURATION_MS = 30 * 24 * 60 * 60 * 1000;
const IMPRESSION_EVENT = "job_in_progress_info_card_impression";
const DISMISS_EVENT = "job_in_progress_info_cards_dismiss";

type InfoCard = {
  id: string;
  title: string;
  body: string;
  // If the user has already completed any job with one of these types, the
  // card is filtered out — we only surface doc types they haven't tried.
  hideIfJobType?: JobType[];
};

const ALL_CARDS: InfoCard[] = [
  {
    id: "invoices",
    title: "We also extract invoices, receipts, and bills",
    body:
      "Line items, totals, tax, and vendor info — ready for QuickBooks, Xero, or Excel.",
    hideIfJobType: ["Invoice"],
  },
  {
    id: "tax-forms",
    title: "W-2s, 1099s, and other tax forms",
    body:
      "Every box, straight into Excel. Drop in a stack and we'll handle the rest.",
    hideIfJobType: ["Form"],
  },
  {
    id: "checks",
    title: "Got a stack of check images?",
    body:
      "We pull the MICR line, payee, amount, date, and memo from every check.",
  },
  {
    id: "brokerage",
    title: "Brokerage statements work too",
    body:
      "Schwab, Fidelity, Vanguard, and 30+ more — holdings, transactions, and cost basis.",
  },
  {
    id: "credit-cards",
    title: "All major credit card statements",
    body:
      "BoA, Chase, Amex, Capital One, Citi, Discover — same upload, same export options.",
  },
];

const wasRecentlyDismissed = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.localStorage.getItem(DISMISS_STORAGE_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_DURATION_MS;
  } catch {
    return false;
  }
};

/**
 * Auto-rotating informational deck shown on the job-in-progress screen.
 * Tells users about doc types they haven't tried yet (invoices, checks,
 * tax forms, brokerage statements, credit card statements). No upgrade
 * ask — pure awareness.
 *
 * Gates (all must hold):
 *  - >= SHOW_AFTER_MS since job.createdAt (skip flash on fast jobs)
 *  - not dismissed in the last 30 days
 *  - at least one card survives the personalization filter
 */
export const JobInProgressInfoCards: React.FC<{ job: Job }> = ({ job }) => {
  const userJobs = useSelector((state: ReduxState) => state.Jobs.jobs);

  const cards = React.useMemo<InfoCard[]>(() => {
    const usedTypes = new Set<JobType>(
      (userJobs || []).map((j) => j.type).filter(Boolean) as JobType[]
    );
    return ALL_CARDS.filter(
      (c) => !c.hideIfJobType?.some((t) => usedTypes.has(t))
    );
  }, [userJobs]);

  const [waitedLongEnough, setWaitedLongEnough] = React.useState<boolean>(
    () => moment().diff(moment(job.createdAt)) >= SHOW_AFTER_MS
  );
  const [dismissed, setDismissed] = React.useState<boolean>(() =>
    wasRecentlyDismissed()
  );
  const [index, setIndex] = React.useState(0);
  const seenIdsRef = React.useRef<Set<string>>(new Set());

  React.useEffect(() => {
    if (waitedLongEnough) return;
    const elapsed = moment().diff(moment(job.createdAt));
    const remaining = Math.max(SHOW_AFTER_MS - elapsed, 0);
    const id = window.setTimeout(() => setWaitedLongEnough(true), remaining);
    return () => window.clearTimeout(id);
  }, [job.createdAt, waitedLongEnough]);

  const shouldShow = waitedLongEnough && !dismissed && cards.length > 0;

  React.useEffect(() => {
    if (!shouldShow || cards.length <= 1) return;
    const id = window.setInterval(
      () => setIndex((i) => (i + 1) % cards.length),
      ROTATE_MS
    );
    return () => window.clearInterval(id);
  }, [shouldShow, cards.length]);

  const current = shouldShow ? cards[index % cards.length] : null;

  React.useEffect(() => {
    if (!current) return;
    if (seenIdsRef.current.has(current.id)) return;
    seenIdsRef.current.add(current.id);
    capturePostHogEvent(IMPRESSION_EVENT, {
      jobId: job.id,
      cardId: current.id,
    });
  }, [current, job.id]);

  if (!current) return null;

  const onDismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_STORAGE_KEY, Date.now().toString());
    } catch {
      /* ignore */
    }
    setDismissed(true);
    capturePostHogEvent(DISMISS_EVENT, { jobId: job.id });
  };

  return (
    <Card className="mt-4" style={{ maxWidth: "640px" }}>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start">
          <div className="mr-3">
            <div className="text-uppercase text-muted small mb-1">
              Did you know?
            </div>
            <div className="font-weight-bold mb-1">{current.title}</div>
            <div className="text-muted small">{current.body}</div>
          </div>
          <button
            type="button"
            className="btn btn-link btn-sm text-muted p-0"
            onClick={onDismiss}
            aria-label="Dismiss"
          >
            &times;
          </button>
        </div>
        {cards.length > 1 && (
          <div className="d-flex mt-3" aria-label="Card progress">
            {cards.map((c, i) => (
              <div
                key={c.id}
                style={{
                  flex: 1,
                  height: "3px",
                  marginRight: i === cards.length - 1 ? 0 : "4px",
                  backgroundColor:
                    i === index % cards.length ? "#5c6bc0" : "#e0e0e0",
                  borderRadius: "2px",
                  transition: "background-color 0.3s",
                }}
              />
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default JobInProgressInfoCards;
